import { SUBSCRIPTION_EXP_ENABLED_HEADER } from '@src/constants/api.headers.constants'
import { createBatches } from '@src/utils/general.utils'

import Product from '../../domains/Product/Product'
import QuinceApiError from '../../domains/quince-api-error'
import filterFalsy from '../../utils/filterFalsy'
import { getContentProductsEnv } from '../../utils/getEnvironmentParam'
import { sandServicesHttpClient } from '../../utils/HttpClient'

import { adaptEnrichV2Product } from './adapt-enrich-v2-product'
import { IEnrichV2Product } from './enrich-v2.types'

export interface GetProductV2Request {
  /**
   * INTERNAL product ids. v2 renamed the wire param: `productIds` here maps to
   * what v1 called `internalProductIds` (v1's `productIds` meant Shopify SPIDs).
   */
  productIds?: (number | string)[]
  useLite?: boolean
  extraParams?: object
}

export type GetProductV2RequestWithProductIds = GetProductV2Request & Required<Pick<GetProductV2Request, 'productIds'>>

/**
 * Smart Variant / VDA v2 enrich — `GET /products/v2/enrich`. Called only when
 * the SVD rollout experiment (`ramp_svd-rollout-web`) resolves to `show` (see cart.service.getProductData);
 * flag-off traffic stays on the v1 `fetchAndEnrichProducts` path untouched.
 *
 * Responses are adapted back to the v1 `IProduct` shape (svid/spid renames,
 * options/title synthesis from `vdas`) so consumers work unchanged.
 * No `/b2b` prefix: B2B self-serve remains on v1 in this phase.
 */
export async function fetchAndEnrichProductsV2(
  fetchProductRequest?: GetProductV2Request,
  subscriptionExpEnabled?: boolean,
): Promise<Product[]> {
  // useLite defaults to false: the trimmed v2 lite response is still being
  // built by BE — every v2 caller fetches the full payload until BE confirms
  // lite keeps vdas / attributeValues / media / status / ids.
  const { productIds = undefined, useLite = false, extraParams = undefined } = fetchProductRequest ?? {}
  if ((productIds?.length ?? 0) === 0) {
    return []
  }

  let productList: IEnrichV2Product[] = []
  try {
    const { data: products } = await sandServicesHttpClient.get<IEnrichV2Product[]>(`/products/v2/enrich`, {
      params: {
        ...extraParams,
        productIds: productIds?.join(','),
        environment: getContentProductsEnv(),
        useLite,
      },
      headers: {
        [SUBSCRIPTION_EXP_ENABLED_HEADER]: subscriptionExpEnabled ? 'true' : 'false',
      },
    })
    productList = products
  } catch (e) {
    throw new QuinceApiError({
      error: e as Error,
      message: 'Failed while fetching products v2 enrich fetchAndEnrichProductsV2() runtime',
      apiName: 'PRODUCTS_ENRICH_V2_FETCH',
      data: {
        fetchProductRequest,
      },
    })
  }

  return filterFalsy(productList).map(product => new Product(adaptEnrichV2Product(product)))
}

export async function fetchAndEnrichProductsV2UsingBatching(
  fetchProductRequest: GetProductV2RequestWithProductIds,
  subscriptionExpEnabled?: boolean,
): Promise<Product[]> {
  const batches = createBatches(fetchProductRequest.productIds)
  const products = await Promise.all(
    batches.map(batch =>
      fetchAndEnrichProductsV2({ ...fetchProductRequest, productIds: batch }, subscriptionExpEnabled),
    ),
  )
  return products.flat()
}
